import React, { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ArrowDownIcon } from "@heroicons/react/outline";
import { Controller } from "react-hook-form";
import classNames from "lib/classNames";
import useField from "lib/useField";
import useTranslation from "next-translate/useTranslation";

export default function Select({ control, errors, field }) {
  const { t } = useTranslation("common");
  const { id, name, disabled, required, requiredMessage, options } =
    useField(field);
  const hasError = errors[name] || false;

  if (options === null) {
    return null;
  }

  return (
    <>
      <label
        htmlFor={id}
        className={classNames(
          hasError && "text-red-500",
          "inline-block mb-1 font-important font-medium uppercase text-sm"
        )}
      >
        {field["#title"]}
        {required && <span className="text-primary"> *</span>}
      </label>
      <Controller
        id={id}
        name={name}
        control={control}
        rules={{
          required: required && requiredMessage,
          disabled,
        }}
        render={({ field: { value, onChange } }) => (
          <>
            <Listbox value={value} onChange={onChange}>
              {({ open }) => (
                <div className="relative w-full rounded shadow-spread">
                  <Listbox.Button
                    className={classNames(
                      hasError ? "border-red-500" : "border-gray-800",
                      "group text-left flex items-center w-full py-4 px-6 bg-primary text-white font-semibold rounded outline-none focus:bg-primary-dark hover:bg-primary-dark transition-colors"
                    )}
                  >
                    <span
                      className={classNames(
                        value && value !== "" && "border-secondary",
                        "block truncate flex-1"
                      )}
                    >
                      {value &&
                      value !== "" &&
                      options.find((val) => val.value === value)
                        ? options.find((val) => val.value === value).key
                        : t("form.subject.placeholder")}
                    </span>
                    <span className="pointer-events-none p-2 bg-white/20 rounded-full">
                      <ArrowDownIcon className="h-4 w-4 text-white" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options
                      static
                      className="absolute z-10 max-h-60 w-full py-2 overflow-auto bg-secondary-light shadow-spread focus:outline-none"
                    >
                      {options.map((option) => (
                        <Listbox.Option
                          key={option.id}
                          className={({ active }) =>
                            classNames(
                              active && "text-primary",
                              "relative cursor-default select-none px-6 py-3"
                            )
                          }
                          value={option.value}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={classNames(
                                  selected
                                    ? "pl-6 font-semibold"
                                    : "font-semibold",
                                  "block truncate font-semibold"
                                )}
                              >
                                {option.key}
                              </span>
                              {selected ? (
                                <span
                                  className="absolute inset-y-0 left-0 flex items-center pl-2.5"
                                  aria-hidden="true"
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>
            {errors[name] && (
              <p className="text-sm text-red-500">{errors[name].message}</p>
            )}
          </>
        )}
      />
    </>
  );
}
